import React, { useState } from 'react';

const VerificationsQuickActionModal = ({ record, onClose, onSave, currentUser }) => {
  const [formData, setFormData] = useState({
    action_type: 'verification_completed',
    description: '',
    details: '',
    new_status: record?.status || 'pendiente'
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      alert('Por favor, describe la acción realizada');
      return;
    }

    setLoading(true);
    
    try {
      // Preparar los datos de la acción para el callback del componente padre
      const actionData = {
        action_type: formData.action_type,
        old_status: record.status,
        new_status: formData.new_status,
        comment: formData.description,
        details: formData.details,
        user_name: currentUser?.firstName && currentUser?.lastName 
          ? `${currentUser.firstName} ${currentUser.lastName}` 
          : currentUser?.email || 'Usuario'
      };

      // Llamar al callback del componente padre que maneja toda la lógica
      if (onSave) {
        await onSave(record.id, actionData);
      }

      // Si llegamos aquí, todo salió bien
      onClose();
    } catch (error) {
      console.error('Error en acción rápida:', error);
      alert('Error al realizar la acción: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const actionTypes = [
    { value: 'verification_completed', label: 'Verificación Completada', icon: '✅' },
    { value: 'follow_up', label: 'Seguimiento Realizado', icon: '📞' },
    { value: 'email_sent', label: 'Email Enviado', icon: '📧' },
    { value: 'meeting_scheduled', label: 'Reunión Programada', icon: '📅' },
    { value: 'issue_found', label: 'Problema Encontrado', icon: '⚠️' },
    { value: 'validation_passed', label: 'Validación Aprobada', icon: '🎯' },
    { value: 'comment_added', label: 'Comentario Agregado', icon: '💬' },
    { value: 'status_changed', label: 'Cambio de Estado', icon: '🔄' }
  ];

  const statusOptions = [
    { value: 'pendiente', label: '⏳ Pendiente', color: 'text-yellow-600' },
    { value: 'en_proceso', label: '🔄 En Proceso', color: 'text-blue-600' },
    { value: 'completado', label: '✅ Completado', color: 'text-green-600' },
    { value: 'cancelado', label: '❌ Cancelado', color: 'text-red-600' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">
                🚀 Acción Rápida
              </h2>
              <p className="text-purple-100 text-sm">
                Cliente: {record?.nombre_cliente}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-purple-200 transition-colors duration-200"
              disabled={loading}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Información del registro */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 mb-6 border border-purple-200">
            <h3 className="font-semibold text-purple-800 mb-2">Información de la Verificación</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Cliente:</span>
                <span className="ml-2 text-gray-900">{record?.nombre_cliente}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Célula:</span>
                <span className="ml-2 text-gray-900">{record?.celula}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Campaña:</span>
                <span className="ml-2 text-gray-900">{record?.campana}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Estado Actual:</span>
                <span className="ml-2 text-gray-900">{record?.status}</span>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* Tipo de Acción */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Tipo de Acción *
              </label>
              <div className="grid grid-cols-1 gap-2">
                {actionTypes.map((type) => (
                  <label
                    key={type.value}
                    className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                      formData.action_type === type.value
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-300 hover:border-purple-300 hover:bg-purple-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="action_type"
                      value={type.value}
                      checked={formData.action_type === type.value}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <span className="text-xl mr-3">{type.icon}</span>
                    <span className="font-medium">{type.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Cambio de Estado (solo si se selecciona status_changed) */}
            {formData.action_type === 'status_changed' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nuevo Estado *
                </label>
                <select
                  name="new_status"
                  value={formData.new_status}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  {statusOptions.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Descripción de la Acción */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descripción de la Acción *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder={`Describe brevemente la acción realizada...`}
              />
            </div>

            {/* Detalles Adicionales */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Detalles Adicionales
              </label>
              <textarea
                name="details"
                value={formData.details}
                onChange={handleChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Información adicional, próximos pasos, observaciones..."
              />
            </div>

            {/* Información del Cliente */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Información del Cliente</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Cliente:</span>
                  <span className="ml-2 font-medium">{record?.nombre_cliente}</span>
                </div>
                <div>
                  <span className="text-gray-500">Estado Actual:</span>
                  <span className="ml-2 font-medium">
                    {statusOptions.find(s => s.value === record?.status)?.label || record?.status}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Célula:</span>
                  <span className="ml-2 font-medium">{record?.celula || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Campaña:</span>
                  <span className="ml-2 font-medium">{record?.campana || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !formData.description.trim()}
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Guardando...</span>
                </>
              ) : (
                <>
                  {actionTypes.find(t => t.value === formData.action_type)?.icon}
                  <span>Registrar Acción</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VerificationsQuickActionModal;
